
import React from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Printer, Calendar, Clock, Award } from "lucide-react";
import { format } from "date-fns";
import { printStudentEnrollment } from '@/utils/printing/studentEnrollmentPrinter';
import { Student, Program } from "@/services/db/types";

interface ProgramItemProps {
  program: Program;
  student: Student;
}

const ProgramItem = ({ program, student }: ProgramItemProps) => {
  const handlePrintEnrollment = () => {
    printStudentEnrollment(student);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h4 className="font-semibold text-lg">{program.title}</h4>
            <Badge variant="outline" className="text-xs">
              {program.category}
            </Badge>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-600">Fee Status:</span>
                <Badge className={getStatusColor(program.feeStatus || 'pending')}>
                  {program.feeStatus || 'pending'}
                </Badge>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-600">Program Fee:</span>
                <span>AFN {program.fee || 0}</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                <span className="font-medium text-gray-600">Enrolled:</span>
                <span>{program.enrolledAt ? format(new Date(program.enrolledAt), 'PP') : 'N/A'}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-500" />
                <span className="font-medium text-gray-600">Completion:</span>
                <span>{program.completionDate ? format(new Date(program.completionDate), 'PP') : 'N/A'}</span>
              </div>
            </div>
          </div>
          
          <div className="mt-3 flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-600">Remaining Days:</span>
              <span>{program.duration || 0} days</span>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-600">Program Progress:</span>
              <span>0%</span>
            </div>
          </div>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrintEnrollment}
          className="flex items-center gap-2"
        >
          <Printer className="h-4 w-4" />
          Print Enrollment
        </Button>
      </div>
      
      <div className="border-t pt-3">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <Award className="h-4 w-4 text-gray-500" />
            <span className="font-medium text-gray-600">Certification Requirements:</span>
            <Badge variant="secondary" className="text-xs">
              79% Complete
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramItem;
